<template>
  <div v-if="items.length > 0 || flash" class="rb">
    <div v-for="item in items" :key="item.key" class="rb-bar" :class="'rb-bar--' + item._type">

      <!-- In revisione -->
      <div v-if="item._type === 'pending'" class="rb-row">
        <div class="rb-dots"><span></span><span></span><span></span></div>
        <span class="rb-title">{{ $t('reviewBar.underReview') }}</span>
      </div>

      <!-- Rifiutata -->
      <div v-else-if="item._type === 'rejected'" class="rb-row">
        <svg class="rb-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
        </svg>
        <span class="rb-title">{{ $t('betWasNotAccepted') }}</span>
        <button class="rb-close" @click="removeBetFromRejectedBets(item.code)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
        <div class="rb-progress" :style="{ '--rb-dur': AUTO_CLOSE_SEC + 's' }"></div>
      </div>

      <!-- Riproposta dell'operatore (importo e/o quota) -->
      <template v-else-if="item._type === 'change'">
        <div class="rb-row">
          <svg class="rb-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>
          </svg>
          <span class="rb-title">{{ $t(changeLabelKey(item)) }}</span>
        </div>

        <div class="rb-details">
          <div class="rb-detail">
            <span class="rb-detail__label">{{ $t('amount') }}</span>
            <span class="rb-detail__value">
              <template v-if="hasAmountChanged(item)">
                <span class="rb-old">{{ getSettings.currencySymbol }} {{ item.amount | round_digits(2) }}</span>
                <span class="rb-arrow">&rarr;</span>
              </template>
              {{ getSettings.currencySymbol }} {{ item.amount_real | round_digits(2) }}
            </span>
          </div>
          <div v-for="odd in changedOdds(item)" :key="'o-' + (odd.unique_id || odd.outcome_id)" class="rb-detail">
            <span class="rb-detail__label rb-detail__label--ellipsis">{{ odd.event_description || $t('odd') }}</span>
            <span class="rb-detail__value">
              <span class="rb-old">{{ parseFloat(odd.outcome_odd_value_for_order) | odds_type_filter }}</span>
              <span class="rb-arrow">&rarr;</span>
              {{ parseFloat(odd.outcome_odd_value) | odds_type_filter }}
            </span>
          </div>
          <div class="rb-detail">
            <span class="rb-detail__label">{{ $t('potentialWinnings') }}</span>
            <span class="rb-detail__value">{{ getSettings.currencySymbol }} {{ item.winning_total | round_digits(2) }}</span>
          </div>
        </div>

        <div class="rb-actions">
          <button class="rb-btn rb-btn--reject" :disabled="!!loadingActions[item.code]" @click="rejectBetReview(item.code)">
            <svg v-if="loadingActions[item.code] === 'reject'" class="rb-spin" width="14" height="14" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" style="opacity:.25"/>
              <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
            </svg>
            <span v-else>{{ $t('reviewBar.reject') }}</span>
          </button>
          <button class="rb-btn rb-btn--accept" :disabled="!!loadingActions[item.code]" @click="acceptBetReview(item.code)">
            <svg v-if="loadingActions[item.code] === 'accept'" class="rb-spin" width="14" height="14" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" style="opacity:.25"/>
              <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
            </svg>
            <span v-else>{{ $t('reviewBar.accept') }}</span>
          </button>
        </div>
      </template>
    </div>

    <!-- Esito delle azioni sulla riproposta -->
    <div v-if="flash" class="rb-bar" :class="flash.error ? 'rb-bar--rejected' : 'rb-bar--info'">
      <div class="rb-row">
        <span class="rb-title">{{ flash.text }}</span>
        <button class="rb-close" @click="flash = null">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'ReviewBarAmerican',

  data: () => ({
    AUTO_CLOSE_SEC: 6,
    rejectTimers:   {},
    loadingActions: {}, // { [code]: 'accept' | 'reject' }
    flash:          null, // { text, error }
    flashTimer:     null,
  }),

  computed: {
    ...mapGetters({
      getAcceptingBets:         'cart/getAcceptingBets',
      getAcceptedBets:          'cart/getAcceptedBets',
      getAcceptedBetWithChange: 'cart/getAcceptedBetWithChange',
      getRejectedBets:          'cart/getRejectedBets',
      getSettings:              'cart/getSettings',
    }),

    items() {
      const all = [];
      (this.getAcceptingBets         || []).forEach(b => all.push({ ...b, _type: 'pending',  key: 'p_' + b.code }));
      (this.getAcceptedBetWithChange || []).forEach(b => all.push({ ...b, _type: 'change',   key: 'c_' + b.code }));
      (this.getRejectedBets          || []).forEach(b => all.push({ ...b, _type: 'rejected', key: 'r_' + b.code }));
      return all;
    },
  },

  watch: {
    // Accettata: il socket ha già impostato la confirmed bet (stessa schermata
    // della scommessa piazzata), la entry non serve più.
    getAcceptedBets: {
      immediate: true,
      handler(val) {
        (val || []).forEach(b => this.removeBetFromAcceptedBets(b.code));
      },
    },

    // Rifiutata: la barra rossa si chiude da sola dopo AUTO_CLOSE_SEC
    getRejectedBets: {
      immediate: true,
      handler(val) {
        const codes = (val || []).map(b => String(b.code));
        codes.forEach(code => {
          if (this.rejectTimers[code]) return;
          this.rejectTimers[code] = setTimeout(() => {
            delete this.rejectTimers[code];
            this.removeBetFromRejectedBets(code);
          }, this.AUTO_CLOSE_SEC * 1000);
        });
        Object.keys(this.rejectTimers).forEach(code => {
          if (codes.includes(code)) return;
          clearTimeout(this.rejectTimers[code]);
          delete this.rejectTimers[code];
        });
      },
    },
  },

  beforeDestroy() {
    Object.values(this.rejectTimers).forEach(id => clearTimeout(id));
    if (this.flashTimer) clearTimeout(this.flashTimer);
  },

  methods: {
    ...mapActions({
      removeBetFromAcceptedBets:       'cart/removeBetFromAcceptedBets',
      removeBetFromRejectedBets:       'cart/removeBetFromRejectedBets',
      removeBetFromAcceptedWithChange: 'cart/removeBetFromAcceptedWithChange',
      acceptsRevisedBet:               'cart/acceptsRevisedBet',
      rejectRevisedBet:                'cart/rejectRevisedBet',
      setConfirmedBet:                 'cart/setConfirmedBet',
    }),

    // True quando l'importo proposto (amount_real) differisce da quello del player
    hasAmountChanged(item) {
      if (!item || item.amount == null || item.amount_real == null) return false;
      const original = parseFloat(item.amount);
      const proposed = parseFloat(item.amount_real);
      if (isNaN(original) || isNaN(proposed)) return false;
      return Math.abs(original - proposed) > 0.001;
    },

    hasOddChanged(odd) {
      if (!odd || odd.outcome_odd_value_for_order == null) return false;
      const requested = parseFloat(odd.outcome_odd_value_for_order);
      const proposed  = parseFloat(odd.outcome_odd_value);
      if (isNaN(requested) || isNaN(proposed)) return false;
      return Math.abs(requested - proposed) > 0.001;
    },

    changedOdds(item) {
      if (!item || !Array.isArray(item.odds)) return [];
      return item.odds.filter(this.hasOddChanged);
    },

    changeLabelKey(item) {
      const amt = this.hasAmountChanged(item);
      const odd = this.changedOdds(item).length > 0;
      if (amt && odd) return 'proposalChangeAmountAndOdd';
      if (odd)        return 'proposalChangeOdd';
      return 'proposalChangeAmount';
    },

    async acceptBetReview(code) {
      if (this.loadingActions[code]) return;
      this.$set(this.loadingActions, code, 'accept');

      try {
        const item = this.items.find(b => b.code === code && b._type === 'change');
        const res  = await this.acceptsRevisedBet({ code });
        if (!this._handleReviewResult(res, code)) return;

        // Stesso effetto della scommessa piazzata, con importo/quote della riproposta
        if (item) {
          await this.setConfirmedBet({
            bet_code:         item.code,
            is_booked:        false,
            type:             item.type || 'single',
            amount:           item.amount_real != null ? item.amount_real : (item.amount || 0),
            odds_total:       item.odds_total || 0,
            winning:          item.winning || 0,
            winning_total:    item.winning_total || 0,
            bonus:            item.bonus || 0,
            bonus_percentage: item.bonus_percentage || 0,
            odds:             item.odds || [],
          });
        }
      } finally {
        this.$set(this.loadingActions, code, null);
      }
    },

    async rejectBetReview(code) {
      if (this.loadingActions[code]) return;
      this.$set(this.loadingActions, code, 'reject');

      try {
        const res = await this.rejectRevisedBet({ code });
        if (this._handleReviewResult(res, code)) {
          this._showFlash(this.$t('betRevivedNotAccepted'));
        }
      } finally {
        this.$set(this.loadingActions, code, null);
      }
    },

    // La riproposta viene sempre rimossa dallo store, anche se l'API va in errore
    _handleReviewResult(res, code) {
      const data = (res && res.data) || {};
      this.removeBetFromAcceptedWithChange(code);
      if (data.result) return true;

      this._showFlash(
        this.$t(data.error_code === 'BET_NOT_IN_REVIEW' ? 'betNotInReview' : 'betReviewActionError'),
        true,
      );
      return false;
    },

    _showFlash(text, error = false) {
      this.flash = { text, error };
      if (this.flashTimer) clearTimeout(this.flashTimer);
      this.flashTimer = setTimeout(() => { this.flash = null; }, 4000);
    },
  },
};
</script>

<style scoped>
.rb-bar {
  position: relative;
  color: #ffffff;
  padding: 8px 12px;
  overflow: hidden;
}
.rb-bar--pending  { background: #16a34a; }
.rb-bar--change   { background: #b45309; }
.rb-bar--rejected { background: #dc2626; }
.rb-bar--info     { background: #374151; }

.rb-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 20px;
}
.rb-icon { flex-shrink: 0; }
.rb-title {
  flex: 1;
  min-width: 0;
  font-size: var(--font-size-3xl);
  font-weight: 700;
}

.rb-close {
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 2px;
  display: flex;
  opacity: 0.85;
}
.rb-close:hover { opacity: 1; }

/* Puntini animati (in revisione) */
.rb-dots {
  display: flex;
  gap: 3px;
  flex-shrink: 0;
}
.rb-dots span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #ffffff;
  animation: rb-pulse 1.2s infinite ease-in-out;
}
.rb-dots span:nth-child(2) { animation-delay: 0.2s; }
.rb-dots span:nth-child(3) { animation-delay: 0.4s; }
@keyframes rb-pulse {
  0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
  40%           { opacity: 1;   transform: scale(1); }
}

/* Countdown di chiusura (rifiutata) */
.rb-progress {
  position: absolute;
  left: 0;
  bottom: 0;
  height: 3px;
  width: 100%;
  background: rgba(255, 255, 255, 0.6);
  transform-origin: left;
  animation: rb-shrink var(--rb-dur) linear forwards;
}
@keyframes rb-shrink {
  from { transform: scaleX(1); }
  to   { transform: scaleX(0); }
}

/* Riproposta */
.rb-details {
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.rb-detail {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: var(--font-size-2xl);
}
.rb-detail__label { opacity: 0.85; }
.rb-detail__label--ellipsis {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rb-detail__value {
  font-weight: 700;
  white-space: nowrap;
}
.rb-old {
  opacity: 0.7;
  text-decoration: line-through;
  font-weight: 400;
}
.rb-arrow { margin: 0 4px; }

.rb-actions {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}
.rb-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-size: var(--font-size-2xl);
  font-weight: 700;
  transition: opacity 0.15s;
}
.rb-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.rb-btn--accept { background: #ffffff; color: #b45309; }
.rb-btn--reject { background: rgba(0, 0, 0, 0.25); color: #ffffff; }
.rb-spin { animation: rb-rotate 0.8s linear infinite; }
@keyframes rb-rotate { to { transform: rotate(360deg); } }
</style>
